/**
 * Phonetic / multilingual parse of coach planning-board voice commands.
 * Resolves against the focused station/block when names are omitted.
 */

import { normalizeTranscription } from "../voice/TranscriptionNormalizer";
import { exerciseLabel } from "./planningSubgroups";

const COMMAND_PHRASES = {
  add_exercise: [
    "add exercise",
    "add an exercise",
    "new exercise",
    "insert exercise",
    "ajouter exercice",
    "ajouter un exercice",
    "nouvel exercice",
    "ajouter un bloc",
    "exercice hinzufugen",
    "übung hinzufügen",
    "ubung hinzufugen",
    "neue ubung",
    "neue übung",
    "anadir ejercicio",
    "añadir ejercicio",
    "nuevo ejercicio",
    "oefening toevoegen",
    "nieuwe oefening",
    "adicionar exercicio",
    "adicionar exercício",
    "novo exercicio",
    "novo exercício",
  ],
  edit_exercise: [
    "edit exercise",
    "edit this exercise",
    "edit the exercise",
    "edit block",
    "edit",
    "modify exercise",
    "change duration",
    "ouvrir edition",
    "ouvrir édition",
    "modifier exercice",
    "modifier cet exercice",
    "modifier",
    "editer exercice",
    "éditer exercice",
    "editer",
    "éditer",
    "exercice bearbeiten",
    "ubung bearbeiten",
    "übung bearbeiten",
    "bearbeiten",
    "editar ejercicio",
    "modificar ejercicio",
    "editar",
    "oefening bewerken",
    "bewerk oefening",
    "bewerken",
    "editar exercicio",
    "editar exercício",
  ],
  add_sequence: [
    "add sequence",
    "add a sequence",
    "new sequence",
    "ajouter sequence",
    "ajouter séquence",
    "ajouter une sequence",
    "ajouter une séquence",
    "nouvelle sequence",
    "nouvelle séquence",
    "sequence hinzufugen",
    "sequenz hinzufügen",
    "anadir secuencia",
    "añadir secuencia",
    "nueva secuencia",
    "sequentie toevoegen",
    "nieuwe sequentie",
    "adicionar sequencia",
    "adicionar sequência",
  ],
  apply_template: [
    "apply template",
    "use template",
    "open template",
    "load template",
    "appliquer modele",
    "appliquer modèle",
    "utiliser modele",
    "utiliser modèle",
    "ouvrir modele",
    "ouvrir modèle",
    "vorlage anwenden",
    "vorlage verwenden",
    "plantilla",
    "usar plantilla",
    "aplicar plantilla",
    "sjabloon toepassen",
    "sjabloon gebruiken",
    "aplicar modelo",
    "usar modelo",
  ],
  create_personalized: [
    "personalized exercise",
    "personalized training",
    "personalized slot",
    "add personalized",
    "create personalized",
    "exercice personnalise",
    "exercice personnalisé",
    "entrainement personnalise",
    "entraînement personnalisé",
    "personnalise",
    "personnalisé",
    "personalisierte ubung",
    "personalisierte übung",
    "ejercicio personalizado",
    "entrenamiento personalizado",
    "gepersonaliseerde oefening",
    "exercicio personalizado",
    "exercício personalizado",
  ],
  create_custom: [
    "create exercise",
    "create a custom exercise",
    "create custom exercise",
    "custom exercise",
    "make an exercise",
    "build an exercise",
    "creer exercice",
    "créer exercice",
    "creer un exercice",
    "créer un exercice",
    "exercice libre",
    "exercice sur mesure",
    "übung erstellen",
    "ubung erstellen",
    "eigene ubung",
    "eigene übung",
    "crear ejercicio",
    "ejercicio libre",
    "oefening maken",
    "vrije oefening",
    "criar exercicio",
    "criar exercício",
    "exercicio livre",
    "exercício livre",
  ],
  select_catalog: [
    "select catalog",
    "choose from catalog",
    "pick from catalog",
    "catalog exercise",
    "find exercise",
    "search exercise",
    "browse catalog",
    "exchange exercise",
    "swap catalog",
    "choisir dans le catalogue",
    "catalogue",
    "chercher exercice",
    "trouver exercice",
    "echanger exercice",
    "échanger exercice",
    "katalog",
    "ubung suchen",
    "übung suchen",
    "catalogo",
    "catálogo",
    "buscar ejercicio",
    "elegir ejercicio",
    "catalogus",
    "oefening zoeken",
    "procurar exercicio",
    "procurar exercício",
  ],
  create_station: [
    "create station",
    "new station",
    "add station",
    "split station",
    "create subgroup",
    "new subgroup",
    "creer station",
    "créer station",
    "nouvelle station",
    "ajouter station",
    "creer sous groupe",
    "créer sous-groupe",
    "nouveau sous groupe",
    "station erstellen",
    "neue station",
    "crear estacion",
    "crear estación",
    "nueva estacion",
    "nueva estación",
    "station maken",
    "nieuwe station",
    "criar estacao",
    "criar estação",
    "nova estacao",
    "nova estação",
  ],
  ai_plan: [
    "ai plan",
    "recommend plan",
    "recommend session",
    "generate plan",
    "session plan",
    "plan ia",
    "plan ai",
    "recommander seance",
    "recommander séance",
    "generer plan",
    "générer plan",
    "plan recommande",
    "plan recommandé",
    "ki plan",
    "plan vorschlagen",
    "plan generar",
    "recomendar sesion",
    "recomendar sesión",
    "ai plan maken",
    "plan aanbevelen",
    "recomendar sessao",
    "recomendar sessão",
  ],
};

/** Longer phrases first so “add a sequence” wins over “add”. */
function phraseEntries() {
  const entries = [];
  for (const [action, phrases] of Object.entries(COMMAND_PHRASES)) {
    for (const phrase of phrases) {
      entries.push({ action, phrase: String(phrase).toLowerCase() });
    }
  }
  entries.sort((a, b) => b.phrase.length - a.phrase.length);
  return entries;
}

const PHRASE_ENTRIES = phraseEntries();

function stripDiacritics(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizeVoiceText(raw) {
  const viaTranscriber = normalizeTranscription(raw || "");
  return stripDiacritics(viaTranscriber || raw || "")
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/[^a-z0-9\s'-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function findMatchedPhrase(normalized) {
  for (const entry of PHRASE_ENTRIES) {
    const needle = stripDiacritics(entry.phrase);
    if (!needle) continue;
    if (normalized === needle || normalized.includes(needle)) {
      return entry;
    }
  }
  return null;
}

function extractRemainder(normalized, phrase) {
  const needle = stripDiacritics(phrase);
  if (!needle) return "";
  const idx = normalized.indexOf(needle);
  if (idx < 0) return normalized;
  return `${normalized.slice(0, idx)} ${normalized.slice(idx + needle.length)}`
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Match a spoken exercise / block name against the session plan list.
 */
export function matchPlanningExercise(normalized, exercises = [], lng = "en") {
  const text = normalizeVoiceText(normalized);
  if (!text || !Array.isArray(exercises) || !exercises.length) return null;
  let best = null;
  for (const ex of exercises) {
    if (!ex?.te_id) continue;
    const labels = [
      exerciseLabel(ex, lng),
      ex.ex_name,
      ex.ex_name_en,
      ex.ex_name_fr,
      ex.ex_name_de,
      ex.ex_name_es,
      blockKindFallback(ex, lng),
    ]
      .map((label) => normalizeVoiceText(label))
      .filter(Boolean);
    for (const label of labels) {
      if (label.length < 3) continue;
      if (text.includes(label) || label.includes(text)) {
        const score = Math.min(label.length, text.length);
        if (!best || score > best.score) best = { exercise: ex, score };
      }
    }
  }
  return best?.exercise || null;
}

function blockKindFallback(ex, lng) {
  try {
    return exerciseLabel(ex, lng);
  } catch {
    return "";
  }
}

/**
 * @returns {null | {
 *   action: string,
 *   raw: string,
 *   normalized: string,
 *   exercise?: object | null,
 *   description?: string,
 *   query?: string,
 *   incomplete?: boolean,
 *   reason?: string,
 *   deferToAi?: boolean,
 * }}
 */
export function parsePlanningVoiceCommand(raw, opts = {}) {
  const normalized = normalizeVoiceText(raw);
  if (!normalized || normalized.length < 2) return null;

  const matched = findMatchedPhrase(normalized);
  if (!matched) return null;

  const remainder = extractRemainder(normalized, matched.phrase);
  const exercises = Array.isArray(opts.exercises) ? opts.exercises : [];
  const lng = opts.language || "en";
  const named = remainder
    ? matchPlanningExercise(remainder, exercises, lng)
    : null;
  const focused =
    opts.selectedExercise ||
    exercises.find((ex) => String(ex?.te_id) === String(opts.selectedTeId || "")) ||
    null;

  if (matched.action === "add_exercise") {
    return {
      action: "add_exercise",
      raw,
      normalized,
      exercise: named || focused || null,
      description: remainder || "",
    };
  }

  if (matched.action === "edit_exercise" || matched.action === "add_sequence") {
    const exercise = named || focused;
    if (!exercise || !exercise.te_id) {
      return {
        action: matched.action,
        raw,
        normalized,
        exercise: null,
        incomplete: true,
        reason: "need_exercise",
      };
    }
    return {
      action: matched.action,
      raw,
      normalized,
      exercise,
    };
  }

  if (matched.action === "apply_template") {
    return {
      action: "apply_template",
      raw,
      normalized,
      exercise: named || focused || null,
    };
  }

  if (matched.action === "create_personalized") {
    return {
      action: "create_personalized",
      raw,
      normalized,
      description: remainder || "",
    };
  }

  if (matched.action === "create_custom") {
    // Free-form description → AI can draft / propose from catalog.
    if (remainder.length >= 8) {
      return {
        action: "create_custom",
        raw,
        normalized,
        description: remainder,
        deferToAi: true,
      };
    }
    return {
      action: "create_custom",
      raw,
      normalized,
      description: remainder || "",
    };
  }

  if (matched.action === "select_catalog") {
    return {
      action: "select_catalog",
      raw,
      normalized,
      exercise: named || focused || null,
      query: remainder || "",
    };
  }

  if (matched.action === "create_station" || matched.action === "ai_plan") {
    return {
      action: matched.action,
      raw,
      normalized,
      description: remainder || "",
    };
  }

  return null;
}

export function planningExerciseVoiceHints(exercises = [], lng = "en") {
  return (exercises || [])
    .map((ex) => exerciseLabel(ex, lng) || ex?.ex_name_en || ex?.ex_name || "")
    .map((name) => String(name || "").trim())
    .filter(Boolean)
    .slice(0, 40);
}
